import {
  Component,
  effect,
  Inject,
  InjectionToken,
  input,
} from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';

import { TranslatePipe } from '@ngx-translate/core';
import {
  MatCard,
  MatCardActions,
  MatCardContent,
  MatCardImage,
} from '@angular/material/card';
import { DialogService } from '../../services/dialog/dialog.service';
import { MatIcon } from '@angular/material/icon';
import { ImageWithDescription } from '../../examinationResult.model';

export interface PictureManager {
  getImages: () => string[];
  addImage: (image: string) => void;
  removeImageWithDescription: (index: number) => void;
  addDescription: (index: number, description: string) => void;
  getDescription: (index: number) => string;
}
export const PICTURE_MANAGER_TOKEN = new InjectionToken<PictureManager>(
  'PictureManagerToken',
);

@Component({
  selector: 'app-upload-pictures',
  imports: [
    MatButton,
    TranslatePipe,
    MatCard,
    MatCardImage,
    MatCardContent,
    MatIcon,
    MatIconButton,
    MatCardActions,
  ],
  templateUrl: './picture-manager.component.html',
  styleUrl: './picture-manager.component.scss',
})
export class PictureManagerComponent {
  isActive = input.required<boolean>();

  imagesWithDescription: ImageWithDescription[] = [];

  constructor(
    protected dialogService: DialogService,
    @Inject(PICTURE_MANAGER_TOKEN) private pictureManger: PictureManager,
  ) {
    this.initializePictures();

    effect(() => {
      if (this.isActive()) {
        if (this.imagesWithDescription.length == 0) {
          this.openPhotoDialog();
        }
      }
    });
  }

  openPhotoDialog() {
    this.dialogService.openCameraDialog(
      this.pictureManger.getImages,
      this.pictureManger.addImage,
      () => this.initializePictures(),
    );
  }

  initializePictures() {
    this.imagesWithDescription = this.pictureManger
      .getImages()
      .map((image: string, index: number) => {
        return {
          image: image,
          description: this.pictureManger.getDescription(index),
        };
      });
  }

  editDescription(i: number) {
    this.dialogService.openInputDialog({
      inputCheckerFn: (input) => input.length <= 255,
      afterCloseFn: (description: string) => {
        this.pictureManger.addDescription(i, description);
        this.initializePictures();
      },
      inputLabel: 'components.upload-pictures.input-label',
      text: 'components.upload-pictures.input-text',
      errorMessage: 'components.upload-pictures.text-too-long-error',
      defaultInput: this.imagesWithDescription[i].description,
    });
  }

  delete(i: number) {
    this.pictureManger.removeImageWithDescription(i);
    this.initializePictures();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0);
        //we only want jpegs as well as in CameraDialogComponent
        const jpegBase64 = canvas.toDataURL('image/jpeg');
        this.pictureManger.addImage(jpegBase64);
        this.initializePictures();
      };
      img.onerror = () => console.error('Image could not be loaded');
    };

    reader.onerror = (error) => console.error('Readerror:', error);
    reader.readAsDataURL(file);
  }
}
